import {
  Component,
  OnInit,
  Compiler,
  ViewChild,
  Injector,
  HostListener,
  Renderer2,
  Inject,
  OnDestroy,
} from '@angular/core';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import {
  Session,
  UIElement,
  Menu,
  Notification,
  NotificationType,
  Page,
  MenuItem,
  Item,
  Tab,
} from '../app/common/entity/entities';
import { ConfirmationService, MessageService } from 'primeng/api';
import { LocalStorage } from './common/decorators/decorators';
import { BrowserStorage, WebStorageUtility, Tools } from './common/utils/tools';
import { TranslateService } from '@ngx-translate/core';
import { SecurityHelper } from './menuItems/security/helper/security.helper';
import {
  trigger,
  state,
  style,
  animate,
  transition,
} from '@angular/animations';
import { environment } from 'src/environments/environment';
import { SecurityClient } from './client/security.client';
import { HelpClient } from './client/help.client';
import { NotificationEvents } from './common/entity/enum/notificationEvents';
import { Guid, UpdateService } from './common/utils/utils';
import { SwUpdate } from '@angular/service-worker';
import { Title } from '@angular/platform-browser';
import { UserManualDirective } from './common/directives/userManual';
import { DatePipe, DOCUMENT } from '@angular/common';

import { BroadcastService } from './common/client/broadcast.service';
import { first, takeUntil, debounceTime } from 'rxjs/operators';
import { CustomerStore } from './menuItems/customerWizard/@state/customer.store';
import { InsightsFilterStateService } from './menuItems/insightsV2/services/insights-filter-state.service';
import { isInsightsRoleAllowed } from './common/entity/enum/roleIds.enum';
import { TrainingVideos } from './common/entity/enum/trainingVideos';
import { TableColumn } from './common/entity/table/tableColumn';
import { TableTypes } from './common/entity/enums';
import { TableBuilder } from './common/builder/gs.table.builder';
import { BehaviorSubject, Subscription } from 'rxjs';

@Component({
  selector: 'app',
  templateUrl: './app.component.html',
  animations: [
    trigger('minMax', [
      state('min', style({ width: '52px' })),
      state('max', style({ width: '*' })),
      transition('min <=> max', animate('300ms ease-in')),
    ]),
    trigger('contentMaxMin', [
      state('min', style({ marginLeft: '300px' })),
      state('max', style({ marginLeft: '52px' })),
      state('maxLoggedOut', style({ marginLeft: '0px' })),
      transition('min <=> max', animate('300ms ease-in')),
      transition('min <=> maxLoggedOut', animate('300ms ease-in'))
    ]),
    trigger('navigationMaxMin', [
      state('min', style({ marginLeft: '0px', paddingRight: '0px' })),
      state('max', style({ marginLeft: '0px', paddingRight: '0px' })),
      transition('min <=> max', animate('300ms ease-in')),
    ]),
  ],
  styleUrls: ['./app.component.scss'],
  providers: [MessageService, SecurityClient, UpdateService],
})
export class AppComponent implements OnInit, OnDestroy {

  Session = Session;
  @ViewChild('user') user;
  @ViewChild('helpButton') helpButton;
  @ViewChild('help') help;
  @ViewChild('trainingVideos') trainingVideos;
  @ViewChild('helpIframe') iframeHelp;
  helpSrc: string;
  @ViewChild('notification') notification;

  // G2 in-app review prompt (#28067) — 30s inactivity trigger (PDP-4).
  @ViewChild('g2Popup') g2Popup;
  private g2IdleTimerId: any = null;
  private g2LastActivityReset = 0;
  private static readonly G2_IDLE_MS = 30000;
  // PDP-8: deliberate positive-moment trigger (see Session.g2MomentReached$).
  private g2MomentSub: Subscription = null;
  private static readonly G2_MOMENT_DEBOUNCE_MS = 1500;

  requiredRoute: string;
  currentRoute: string;
  navigationMinimize = true;
  environmentName = '';
  environment = environment;
  isHelpShown = false;
  isFromNavigation = false;
  TrainingVideos = TrainingVideos;
  showServiceStatusMessageDialog = false;
  serviceStatusMessageItem: Item;
  signalRConnected$ = new BehaviorSubject(false);
  subscribedToServiceStatusMessageEvent: boolean = false;
  hidden: string;
  showNotificationSidebar: boolean = false;
  notificationOverviewReady: boolean = false;

  /**
   * True while the Insights V2 view is the active route. Drives a full-page
   * takeover: the app header, side menu and chat FAB are hidden and the
   * `.app-content` area expands to fill the whole viewport, so Insights renders
   * as a full-screen overlay (matching the prototype's mxv-overlay). Set from
   * `routerEvents` on every NavigationEnd, so it also covers the initial load
   * when Insights is the default landing page.
   */
  isInsightsView = false;

  get serviceStatusMessage() {
    if (environment.portalCode == 'PDP'
      && Session.valid
      && Session.userCode
      && localStorage?.getItem(`serviceStatusMessage_${Session.userCode}`)) {
      var messages = JSON.parse(localStorage.getItem(`serviceStatusMessage_${Session.userCode}`));
      return messages;
    }
    else
      return null;
  }
  get showVideoIcons() {
    return Session.showVideo$.value;
  }

  @LocalStorage('grat_auth') private sessionObject;
  @LocalStorage('tabs') private tabs;

  get showUserDetails() {
    if (
      Session.menu &&
      Session.menu.menuItems.get('controlpanel') &&
      Session.menu.menuItems.get('controlpanel').pages.get('change_my_details')
    )
      return Session.menu.menuItems
        .get('controlpanel')
        .pages.get('change_my_details').data;
  }

  get unreadNotificationCount(): number {
    if (Session.userNotifications && Session.userNotifications.length > 0) {
      return Session.userNotifications.filter(n => !n.Read).length;
    }
    return 0;
  }

  get isSamePage() {
    return Session?.token == '' && this.sessionObject?.result?.body;
  }

  get selectedPage() {
    return Session.menu?.menuItems?.selected?.pages?.selected;
  }

  get showHelp() {
    return Session.menu?.menuItems.values.some(
      (x) => x.data?.UserManuals?.length
    );
  }

  showVideo = true;
  showTrainingVideo = false;

  /**
   * Insights V2 banner state. <c>insightsAsDefault</c> reflects the localStorage
   * flag <c>pdp.insightsAsDefault</c>; toggling it writes back but does not
   * navigate (per UI-13 — applies on next login only).
   *
   * Access gating is currently OFF — see loose-ends.md "Insights V2 access
   * permission flag" for the planned PageObject hookup.
   */
  insightsAsDefault = false;
  static readonly INSIGHTS_DEFAULT_KEY = 'pdp.insightsAsDefault';

  /**
   * AI-Insights trigger visibility. Gated by {@link isInsightsRoleAllowed}: an
   * empty INSIGHTS_ROLE_IDS opens it to ALL logged-in PDP roles; populate the
   * collection to restrict to specific roles. Role membership comes from the login
   * response (Session.roleIds) — not the single primary Role, since a user can hold
   * several roles.
   */
  get canSeeInsights(): boolean {
    return Session.valid && isInsightsRoleAllowed(Session.roleIds);
  }

  showChatMenu = false;
  showChatGPT = false;
  @ViewChild('chatGptPrompt') chatGptPrompt;
  @ViewChild('chatGPtHistory') chatGPTHistory;
  chatGPTMessages = null;
  chatGPTResponse = null;
  showChatGPTSpinner = false;

  // Config Engine AI ChatBot (AskChatBot) lives in its own component now —
  // see ChatBotComponent (<app-chat-bot> in the template).

  serviceStatusMessageColumns: TableColumn[] = [];
  displayEmployeeInfo: boolean;

  constructor(
    private _compiler: Compiler,
    private _injector: Injector,
    private _router: Router,
    private securityClient: SecurityClient,
    private helpClient: HelpClient,
    private translate: TranslateService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private swUpdate: SwUpdate,
    private title: Title,
    private sw: UpdateService,
    private _usermanual: UserManualDirective,
    private renderer: Renderer2,
    private broadcastService: BroadcastService,
    private datePipe: DatePipe,
    @Inject(DOCUMENT) private _document: Document,
    private customerStore: CustomerStore,
    private insightsFilterState: InsightsFilterStateService
  ) {
    this.environmentName = environment.environmentName;

    BrowserStorage.useFor(this);
    this._router.events.subscribe(this.routerEvents.bind(this));
    Session.notifications.onAdded.subscribe((notification) => {
      this.pushNotification(notification as Notification);
    }, this);
    Session.notifications.onRemoved.subscribe((notification) => {
      //this.popNotification(notification as Notification);
    }, this);
  }

  toggleClass(event: any, className: string) {
    //we must use currentTarget instead of target, to manipulate only selected <li> element, not its children
    let parentElement = event.currentTarget.parentElement;

    const hasClass = parentElement.classList.contains(className);
    if (hasClass) {
      this.renderer.removeClass(parentElement, className);
    } else {
      this.renderer.addClass(parentElement, className);
    }

    //Only one menu item can be expanded at a time, except one with selected page
    //if we expand a menu item without selecting page and collapse the entire menu,
    //all menus except the one with selected page should be deselected
    let menuRootElement = parentElement.parentElement;
    menuRootElement.querySelectorAll(".menu-open").forEach(m => {
      if (m != parentElement && m.querySelector("ul").querySelector(".page-selected") == null) {
        this.renderer.removeClass(m, className);
      }
    });

  }

  ngOnInit() {
    // PDP-8: deliberate positive-moment trigger. Feature components emit on genuine
    // success (WF-async ops only on their SignalR WorkflowExecution_Finished success
    // branch, sync ops on their 2xx). Debounce so back-to-back successes (e.g. a WF
    // finish + a reload) open the popup at most once; the gate does the rest.
    this.g2MomentSub = Session.g2MomentReached$
      .pipe(debounceTime(AppComponent.G2_MOMENT_DEBOUNCE_MS))
      .subscribe(() => this.tryOpenG2Popup());

    // Force re-login on refresh — clears the persisted auth + in-memory Session.
    // Skipped when environment.persistAuthOnRefresh is true (local dev convenience
    // only — never enabled in env.qa/staging/prod). If the persisted sessionObject
    // is present we ALSO hydrate Session in-memory so the user stays logged in;
    // otherwise we still clear (no token to restore).
    const persistAuth = (environment as any).persistAuthOnRefresh === true;
    const hasPersistedSession = !!this.sessionObject?.result?.body;
    if (persistAuth && hasPersistedSession) {
      // Local dev: hydrate Session + push the token into BaseClient.headers +
      // reload the menu, so the router does not bounce us to /login and so the
      // very first XHR carries the GratSync-Token header (otherwise endpoints
      // like getAllActiveCustomers / getAllActiveWithType 401 on refresh).
      //
      // Session.init() expects a login-response shape with a live HttpHeaders
      // instance on .result.headers — after a JSON roundtrip through localStorage
      // that HttpHeaders becomes a plain object that has no .get() method, so
      // we wrap it in a small shim that mimics .get() against the plain map.
      const persisted = this.sessionObject;
      const responseLike: any = {
        ...persisted,
        result: {
          ...persisted.result,
          headers: {
            get: (k: string) => {
              const h = persisted.result?.headers;
              if (h && typeof h.get === 'function') return h.get(k);
              return h ? h[k] : null;
            },
          },
        },
      };
      Session.init(responseLike);
      this.securityClient.reinitTokenHeader();
      // Capture the URL the user was on before the refresh — SecurityHelper.getMenu
      // unconditionally navigates to landing (see security.helper.ts:80 "PREVENT
      // NAVIGATING TO LAST ROUTE"), which throws away the route on every HMR cycle.
      // Restoring it here keeps the user on /insights (or wherever they were) across
      // hot reloads in local dev. Skipped for transient routes (root/login) to avoid
      // navigating to half-mounted pages.
      const restoreUrl = this._router.url;
      const shouldRestore = restoreUrl && restoreUrl !== '/' && !restoreUrl.startsWith('/login');
      SecurityHelper.getMenu(this.securityClient, this._router).then(
        () => {
          this.startSignalR();
          if (shouldRestore) {
            this._router.navigateByUrl(restoreUrl);
          }
        },
        () => {
          // Token expired or backend rejected — fall back to login.
          WebStorageUtility.remove(localStorage, 'grat_auth');
          Session.clear();
          this._router.navigate([Session.loginURL]);
        }
      );
    } else {
      WebStorageUtility.remove(localStorage, 'grat_auth');
      WebStorageUtility.remove(localStorage, `serviceStatusMessage_${Session.userCode}`);
      Session.clear();
    }

    this.translate.setDefaultLang('en');
    this.setTitle();
    if (this.swUpdate.isEnabled) {
    }
    this.sw.checkForUpdates();
    const showVideo = localStorage.getItem('showVideo')
      ? JSON.parse(localStorage.getItem('showVideo'))
      : false;
    if (showVideo) Session.showVideo$.next(true);

    Session.authenticateResponseReceived$.subscribe((value) => {
      if (value && environment.portalCode === 'PDP') {
        let s = Session;
        if (s.data.Role === 'Employee')
          this.displayEmployeeInfo = true;
      }
    })

    Session.subscribeToServiceStatusMessageEvent$.subscribe((value) => {
      this.subscribeToSignalRServiceStatusMessage(value);
    });
    Session.accessTokenUpdated$.subscribe((value) => {
      if (value) {
        this.broadcastService.disconnect();
        Session.broadcastingClientId = null;
        this.startSignalR();
      }
    });

    // Insights V2 default-landing preference. Try/catch covers private-mode browsers
    // where localStorage throws on read.
    try {
      this.insightsAsDefault = localStorage.getItem(AppComponent.INSIGHTS_DEFAULT_KEY) === 'true';
    } catch { this.insightsAsDefault = false; }
  }

  /**
   * Navigates to the Insights V2 view from the banner button. Iris-reveal
   * transition animation will be added when the route animation is wired in
   * Phase D — for now this is a plain navigate.
   */
  /**
   * Banner click handler. Captures the click coordinates so the Insights shell
   * can replay a circular clip-path reveal from that origin point — gives a
   * "the page transformed" feel that matches the wireframe mockup's iris-reveal
   * effect. Coordinates are stored in sessionStorage so they survive the router
   * navigation (one-shot — InsightsV2DashboardComponent consumes + clears it).
   */
  openInsights(event?: MouseEvent): void {
    if (event) {
      try {
        const xPct = (event.clientX / window.innerWidth) * 100;
        const yPct = (event.clientY / window.innerHeight) * 100;
        sessionStorage.setItem('iv2.irisOrigin', JSON.stringify({ x: xPct, y: yPct }));
      } catch { /* private mode — animation just falls back to default fade */ }
    }
    // Carry the portal's selected location into AI-Insights so the overlay opens
    // scoped to it instead of the first accessible one; the filter bar reconciles
    // this seed on load and keeps it when valid. Best-effort — if it can't be
    // read, Insights falls back to its own default.
    const selectedCode = Number(Session?.menu?.menuItems?.selected?.items?.selected?.code);
    if (Number.isFinite(selectedCode) && selectedCode > 0) {
      this.insightsFilterState.setCustomerCodes([selectedCode]);
    }
    this._router.navigate(['/insights']);
    // PDP-8 (#28067): deliberate AI Insights engagement (banner click → this handler),
    // a positive quality moment. The g2 popup is hosted at the app root (outside the
    // isInsightsView chrome), so it still shows over the Insights overlay. Debounced;
    // gate + server cadence decide whether it actually opens.
    Session.g2MomentReached$.next();
  }

  /**
   * Toggles the "Insights as default landing page" preference. Writes to
   * localStorage immediately; does NOT navigate (applies on next login per UI-13).
   * Silent fallback for private-mode browsers that block localStorage.
   */
  setInsightsAsDefault(value: boolean): void {
    this.insightsAsDefault = value;
    try {
      localStorage.setItem(AppComponent.INSIGHTS_DEFAULT_KEY, value ? 'true' : 'false');
    } catch { /* private-mode browser — preference is session-only */ }
  }

  clickHelp(event, isFromNavigation?) {
    this.help.toggle(event);
    this.isHelpShown = true;
    this.isFromNavigation = isFromNavigation;
  }

  clickVideo(event, isFromNavigation?) {
    //this.trainingVideos.toggle(event);
    this.showTrainingVideo = true;
  }

  showServiceStatusMessages(event, isFromNavigation?) {

    if (this.serviceStatusMessageColumns.length == 0) {
      this.serviceStatusMessageColumns = TableBuilder.build(
        TableTypes.ActiveServiceStatusMessages
      );
    }
    let data = JSON.parse(localStorage.getItem(`serviceStatusMessage_${Session.userCode}`));
    this.serviceStatusMessageItem = new Item(null, data);

    data.forEach((x, i) => {
      this.serviceStatusMessageItem.setItem(x, i);
    });

    this.showServiceStatusMessageDialog = true;
  }

  serviceStatusMessageDialogClosed(event) {
    this.serviceStatusMessageItem = null;
  }

  //#region UI EVENTS

  routerEvents(event) {
    if (event instanceof NavigationStart) {
      this.handleNavigationStartEvent(event);
    } else if (event instanceof NavigationEnd) {
      // Full-page takeover flag — Insights hides the app chrome (header/menu/
      // chat) and fills the viewport. Compare on the post-redirect URL.
      const url = event.urlAfterRedirects || event.url || '';
      this.isInsightsView = url.split('?')[0].split('#')[0].startsWith('/insights');
      if (
        event.urlAfterRedirects &&
        event.url != event.urlAfterRedirects &&
        !this.Session.menu.items.selected
      ) {
        this.selectModelItem(Session.menu, event.urlAfterRedirects);
      }
    }
  }

  public logOut() {
    let child;
    for (let index = 0; index < this._document.scripts.length; index++) {
      if (
        this._document.scripts[index].src ===
        'https://assets.app.futr.ai/webchat/futrwebchat.js'
      )
        child = this._document.scripts[index];
    }
    if (child != null) this.renderer.removeChild(this._document, child);
    this.subscribedToServiceStatusMessageEvent = false;
    this.navigationMinimize = true;
    WebStorageUtility.remove(localStorage, 'grat_auth');
    WebStorageUtility.remove(localStorage, `serviceStatusMessage_${Session.userCode}`);
    let token = Session.tokenObject.accessToken;
    let refreshToken = Session.refreshToken;
    Session.clear();
    this._router.navigate([Session.loginURL]);
    this.user.hide();

    this.broadcastService.handleLogout(token, refreshToken)
      .then(() => { this.broadcastService.disconnect() })
      .catch(() => { this.broadcastService.disconnect() });

    this.notificationOverviewReady = false;

    this.customerStore.clear();
  }

  userDetails(e) {
    this._router.navigate(['/controlpanel/changeMyDetails']);
  }

  public changeNavigation() {
    this.navigationMinimize = !this.navigationMinimize;
    if (this.navigationMinimize == true) {
      this.hidden = "nav-treeview-hidden";
      var menuRootElement = (<HTMLElement>document.getElementById("sidebarMenu"));
      menuRootElement.querySelectorAll(".menu-open").forEach(m => {
        if (m.querySelector("ul").querySelector(".page-selected") == null) {
          this.renderer.removeClass(m, 'menu-open');
        }
      });
    }
    else
      this.hidden = "";
  }

  public showNavigation() {
    if (this.navigationMinimize == true) {
      this.navigationMinimize = false;
      this.hidden = "";
    }
  }

  @HostListener('window:unload', ['$event'])
  beforeunloadHandler(event) {
    let tabId = this.tabs.find((t) => t == sessionStorage.getItem('tabId'));
    let tabs = this.tabs;
    tabs.splice(this.tabs.indexOf(tabId), 1);
    if (!tabs.length) {
      // Local dev convenience — keep grat_auth across refresh so the user stays
      // logged in. The flag is undefined in env.qa/staging/prod so production
      // still wipes the token on the last tab close, as before.
      if ((environment as any).persistAuthOnRefresh === true) {
        this.tabs = tabs;
        return;
      }
      WebStorageUtility.remove(localStorage, 'grat_auth');
      Session.clear();
    } else this.tabs = tabs;
  }

  //#endregion

  //#region G2 IN-APP REVIEW (#28067)

  // Any user activity resets the 30s idle countdown (throttled — mousemove fires
  // rapidly). PDP only; on other portals this is a no-op.
  @HostListener('document:mousemove')
  @HostListener('document:keydown')
  @HostListener('document:click')
  @HostListener('document:scroll')
  onG2UserActivity() {
    this.resetG2IdleTimer();
  }

  private resetG2IdleTimer() {
    if (environment.portalCode !== 'PDP') return;
    const now = Date.now();
    // Throttle resets to at most once per second so continuous mouse movement
    // doesn't churn the timer; worst case the prompt fires ~1s early.
    if (this.g2IdleTimerId && now - this.g2LastActivityReset < 1000) return;
    this.g2LastActivityReset = now;
    if (this.g2IdleTimerId) clearTimeout(this.g2IdleTimerId);
    this.g2IdleTimerId = setTimeout(
      () => this.onG2Idle(),
      AppComponent.G2_IDLE_MS
    );
  }

  private onG2Idle() {
    // Don't count a running operation as inactivity: a WF sync/data-processing can
    // take up to ~2 min while the user sits idle. If the app is busy, restart the idle
    // countdown instead of prompting — the prompt gets its chance once things settle,
    // and the PDP-8 moment-trigger still fires the popup on the operation's success.
    if (Session.appBusy) {
      this.resetG2IdleTimer();
      return;
    }
    this.tryOpenG2Popup();
  }

  // Single gate shared by the 30s idle trigger (PDP-4) and the deliberate
  // positive-moment trigger (PDP-8). Cadence/opt-out are already folded into
  // Session.g2Review.Promptable server-side (BE-4/6). Do NOT fire while our
  // popup OR the G2 embed is open: interaction inside the cross-origin G2 iframe
  // does not bubble document events to the parent, so the user reads as "idle"
  // and the prompt would pop over the form they're filling.
  private tryOpenG2Popup() {
    if (
      environment.portalCode === 'PDP' &&
      Session.valid &&
      Session.g2Review?.Promptable &&
      this.g2Popup &&
      !this.g2Popup.visible &&
      !this.g2Popup.embedVisible
    ) {
      this.g2Popup.open();
    }
  }

  //#endregion

  //#region SIGNAL R

  private startSignalR() {
    //open signalr connection
    if (!Session.broadcastingClientId && Session.valid) {
      Session.broadcastingClientId = Tools.generateGUID();
      //  BroadcastingClient .start().then(
      //     () => {
      //       BroadcastingClient.on(
      //         null,
      //         NotificationEvents.GeneralNotification,
      //         this.notificationReceived
      //       );
      //     },
      //     () => {
      //       Session.broadcastingClientId = null;
      //     }
      //   );
      this.broadcastService.init().then((data) => {
        const requestId = Guid.newGuid();
        this.broadcastService
          .subscribe({
            customerCode: null,
            event: NotificationEvents.GeneralNotification,
            requestId,
          })
          .pipe(first())
          .subscribe((data) => {
            console.log(`On Subscribe message: ${data}`);
            this.signalRConnected$.next(true);
          });

        this.notificationOverviewReady = true;


        this.broadcastService
          .tokenExpired()
          .pipe(first())
          .subscribe((data) => {
            this.logOut();
          });
      });
    }
  }

  subscribeToSignalRServiceStatusMessage = (subscribeToServiceStatusMessageUpdated: boolean) => {
    if (subscribeToServiceStatusMessageUpdated
      && !this.subscribedToServiceStatusMessageEvent
      && Session.cache.contains('Customers', 'All')) {
      if (this.broadcastService.isConnected()) {
        console.log('Connected to signalR');
        let allActiveCustomers = Session.cache.get('Customers', 'All');
        let distinctGroupCodes = [...new Set(allActiveCustomers.result.Customers.map(obj => obj.GroupCode))];

        distinctGroupCodes.forEach(gCode => {
          if (gCode) {
            this.subscribedToServiceStatusMessageEvent = true;
            this.broadcastService
              .subscribe({
                customerCode: null,
                event: NotificationEvents.ServiceStatusMessage_Updated,
                requestId: gCode.toString(),
                expiresInSeconds: 86400 //one day
              })
              .pipe(takeUntil(this.broadcastService
                .tokenExpired()))
              .subscribe((data) => {
                localStorage.setItem(`serviceStatusMessage_${Session.userCode}`, data.Message);
              });
          }
        });
      }
      else {
        console.log('Not connected to signalR');
        this.signalRConnected$.subscribe(this.subscribeToSignalRServiceStatusMessage);
      }
    }
  }

  notificationReceived(message) {
    console.log('General notification received through SignalR.', message);
    //if (message && message.Message)
    //  ToasterHelper.showInfo(message.Message);
  }

  confirm(event: Event) {
    this.confirmationService.confirm({
      target: event.target,
      message: 'Are you sure that you want to log out of your account?',
      icon: 'pi pi-exclamation-circle',
      acceptButtonStyleClass: 'edit',
      rejectButtonStyleClass: 'p-button-outlined p-button-rounded outline',
      accept: () => {
        this.logOut();
      },
      reject: () => {

      }
    });
  }

  //#endregion

  //#region NOTIFICATION TOASTER
  addSingleToast(
    id: string,
    severity: string,
    summary: string,
    detail: string,
    lifems?
  ) {
    this.messageService.add({
      id: id,
      sticky: lifems ? false : true,
      severity: severity,
      summary: summary,
      detail: detail,
      life: lifems,
    });
  }

  clearAllToasts() {
    this.notification.modal = false;
    this.messageService.clear();
  }

  private pushNotification(notification: Notification) {
    let messageSeverity = 'info';
    switch (notification.type) {
      case NotificationType.Success:
        messageSeverity = 'success';
        this.notification.position = 'bottom-left';
        break;
      case NotificationType.Info:
        messageSeverity = 'info';
        this.notification.position = 'bottom-left';
        break;
      case NotificationType.Warning:
        messageSeverity = 'warn';
        this.notification.position = 'bottom-left';
        break;
      case NotificationType.Error:
        messageSeverity = 'error';
        this.notification.position = 'top-center';
        break;
    }

    if (notification.message || notification.title)
      this.addSingleToast(
        notification.id,
        messageSeverity,
        notification.title,
        notification.message,
        notification.lifems
      );

    if (notification.lifems) {
      setTimeout(() => {
        Session.notifications.remove(notification.id);
      }, notification.lifems);
    }
  }

  private popNotification(notification: Notification) {
    this.clearAllToasts();
  }

  toastClosed(event) {
    Session.notifications.remove(event.message.id);
  }
  //#endregion

  //#region PRIVATE METHODS

  private handleNavigationStartEvent(event) {
    this.manageTabs();
    if (this.isSamePage) {
      this.refreshPage(event);
    } else {
      this.navigateToPage(event);
    }
  }

  hasSupportSubPage(menuItem: any): boolean {
    return menuItem?.pages?.values?.some((p: any) => p.route === 'help/support');
  }

  private refreshSupportBadgeCount() {
    this.helpClient.getSupportBadgeCount().then((response) => {
      Session.supportTicketCount = response?.result?.count || 0;
    }).catch(() => {});
  }

  private refreshPage(event) {
    Session.init(this.sessionObject.result.body);
    Session.requestedRoute = event.url;

    SecurityHelper.getMenu(this.securityClient, this._router).then(
      (response) => {
        this.startSignalR();
        this.selectModelItem(Session.menu, event.url);
        this.refreshSupportBadgeCount();
      },
      (reject) => {
        this._router.navigate([Session.loginURL]);
        this.navigationMinimize = true;
        Session.valid = false;
        Session.token = null;
        Session.tokenObject = null;
      }
    );
  }

  private navigateToPage(event) {
    if (this.isLoginOrSignUp(event)) {
      this.handleLoginOrSignUpPage();
    } else {
      this.startSignalR();
    }

    if (event.url == '/adm_help/help') {
      if (this.showHelp) {
        this.help.toggle(event, this.help.el.nativeElement);
        this.isHelpShown = true;
      }
    } else {
      if (this.sessionExistsAndRouteChanged(event)) {
        this.selectModelItem(Session.menu, event.url);
      }

      this.currentRoute = event.url;
      if (this.hasNoTokenAndNotLoginOrSignUp(event)) {
        if (
          event.url.indexOf('auth-error') == -1 &&
          event.url.indexOf('auth-callback') == -1
        )
          this.rememberRequestedRouteAndNavigateToLogin(event);
        //else this._router.navigateByUrl(this.currentRoute);
      }

      if (
        Session.tokenObject?.accessToken == '' &&
        event.url == '/signup' &&
        environment.portalCode != 'PDP'
      )
        this._router.navigate([Session.loginURL]);
    }
  }

  private sessionExistsAndRouteChanged(event) {
    return this.currentRoute != event.url && Session.menu != null;
  }

  private hasNoTokenAndNotLoginOrSignUp(event) {
    return (
      Session.tokenObject?.accessToken == null && event.url != '/login' && event.url != '/brinker' && event.url != '/signup'
    );
  }

  private rememberRequestedRouteAndNavigateToLogin(event) {
    Session.requestedRoute = event.url;
    if (Session.requestedRoute == '/') Session.requestedRoute = '/home';

    this._router.navigate([Session.loginURL]);
  }

  private isLoginOrSignUp(event) {
    return (
      event.url == '/login' || event.url == '/brinker' ||
      (event.url == '/signup' && environment.portalCode == 'PDP')
    );
  }

  private handleLoginOrSignUpPage() {
    this.navigationMinimize = true;
    Session.valid = false;
    const showVideo = localStorage.getItem('showVideo');
    // Preserve the Insights default-landing flag across the localStorage wipe —
    // otherwise visiting /login (e.g. session expiry, manual logout) resets the
    // user's switch preference back to off.
    const insightsAsDefault = localStorage.getItem(AppComponent.INSIGHTS_DEFAULT_KEY);
    // Same for the Insights light/dark theme choice (#27703) — must survive a
    // logout/session-expiry so the user's theme is remembered on next entry.
    const insightsTheme = localStorage.getItem('pdp.insightsTheme');
    localStorage.clear();
    if (showVideo) localStorage.setItem('showVideo', showVideo);
    if (insightsAsDefault) localStorage.setItem(AppComponent.INSIGHTS_DEFAULT_KEY, insightsAsDefault);
    if (insightsTheme) localStorage.setItem('pdp.insightsTheme', insightsTheme);

    // Clear the in-memory Insights filter state (root singleton survives the
    // SPA logout) so a prior user's location/date/shift can't leak into the
    // next session. Theme is preserved inside reset(). See #27704.
    this.insightsFilterState.reset();
  }

  private selectModelItem(uiElement: UIElement, route: string) {
    let selected = false;
    if (`/${uiElement.route}` != route && route.indexOf(uiElement.route) > -1) {
      uiElement.select();
    }
    if (`/${uiElement.route}` == route) {
      selected = true;
      uiElement.select();
    } else {
      if (uiElement instanceof Menu) {
        uiElement.menuItems?.selected?.pages?.selected?.unselect();

        uiElement.menuItems.values.forEach((mi: MenuItem) => {
          if (!selected)
            selected = this.selectModelItem(mi, route);
        });
      }
      else if (uiElement instanceof MenuItem)
        uiElement.pages.values.forEach((p: Page) => {
          if (!selected)
            selected = this.selectModelItem(p, route);
        });
      else if (uiElement instanceof Page) {
        uiElement.pages.values.forEach((p: Page) => {
          if (!selected)
            selected = this.selectModelItem(p, route);
        });

        uiElement.tabs.values.forEach((t: Tab) => {
          if (!selected)
            selected = this.selectModelItem(t, route);
        });
      }
    }
    return selected;
  }

  private manageTabs() {
    if (!this.tabs) this.tabs = [];
    let tabId;
    if (sessionStorage.getItem('tabId'))
      tabId = sessionStorage.getItem('tabId');
    else {
      tabId = Guid.newGuid().toString();
      sessionStorage.setItem('tabId', tabId);
    }

    if (!this.tabs.some((t) => t == sessionStorage.getItem('tabId'))) {
      let tabs = this.tabs;
      tabs.push(tabId);
      this.tabs = tabs;
    }
  }

  setTitle() {
    if (environment.portalCode == 'PDP') this.title.setTitle('PayDayPortal');
    else if (environment.portalCode == 'PAP')
      this.title.setTitle('Payday admin portal');
  }

  copyAccountID() {
    navigator.clipboard.writeText(Session.accountID).then(
      () => { },
      () => { }
    );
  }

  changeVideoVisibility(e) {
    localStorage.setItem('showVideo', e);
    Session.showVideo$.next(e);
  }

  onToogleNotification(closeNotification?: boolean) {
    if (closeNotification)
      this.showNotificationSidebar = false;
    else
      this.showNotificationSidebar = !this.showNotificationSidebar;
  }

  //#endregion

  ngOnDestroy(): void {
    this.broadcastService.disconnect();
    this.signalRConnected$?.unsubscribe();
    if (this.g2IdleTimerId) clearTimeout(this.g2IdleTimerId);
    this.g2MomentSub?.unsubscribe();
  }

  openChatGPT() {
    this.showChatGPT = true;
    this.showChatMenu = false;
  }

  openChatMenu() {
    this.showChatMenu = true;
  }

  sendChatGPT() {
    var message = this.chatGptPrompt.nativeElement.value;
    this.chatGptPrompt.nativeElement.value = '';
    this.chatGptPrompt.nativeElement.disabled = true;
    if (message != null && message != '') {
      var model = {
        Messages:
          this.chatGPTResponse == null ? [] : this.chatGPTResponse.Messages,
        Prompt: message,
      };
      this.Session.suppressSpinner = true;
      this.showChatGPTSpinner = true;
      this.securityClient
        .sendChatGPT(model)
        .then((response) => {
          this.chatGptPrompt.nativeElement.disabled = false;
          this.Session.suppressSpinner = false;
          this.chatGptPrompt.nativeElement.value = '';
          this.chatGPTMessages = [];
          this.chatGPTResponse = response?.result;
          response?.result?.Messages?.forEach((x) => {
            if (x.Role == 'assistant' || x.Role == 'user') {
              var message = {
                Role: x.Role,
                Content: x.Content,
                Sent: this.datePipe.transform(x.Date, 'HH:mm'),
              };
              this.chatGPTMessages.push(message);
            }
          });
          setTimeout(() => {
            this.chatGPTHistory.nativeElement.scrollTop =
              this.chatGPTHistory.nativeElement.scrollHeight;
          }, 1);
          setTimeout(() => this.chatGptPrompt.nativeElement.focus(), 1);
          this.showChatGPTSpinner = false;
        })
        .catch((reason) => {
          this.chatGptPrompt.nativeElement.disabled = false;
          this.Session.suppressSpinner = false;
          this.showChatGPTSpinner = false;
        });
    }
  }

  closeChat() {
    this.showChatMenu = false;
    this.showChatGPT = false;
  }
}
